/**
 * Visual identity for a person when there is NO photo: initials + a stable
 * color.
 *
 * This is the ONLY implementation of these three functions in the monorepo.
 * Previously each screen resolved initials on its own with `charAt(0)` and
 * different rules (some fell back to the email, some didn't; some uppercased,
 * some didn't), so the same person looked different depending on where you looked.
 *
 * Pure and dependency-free on purpose: it's used by the web, desktop, mobile
 * and the tests, and it has to give EXACTLY the same result in all of them.
 */

/** How many initials the fallback avatar carries. */
const MAX_INITIALS = 2;

/** How many color pairs the palette has (see `avatar.tsx`). */
export const AVATAR_PALETTE_SIZE = 8;

/**
 * Initials for the fallback avatar.
 *
 * Takes up to two, from the first and last word of the name. If there is no
 * name it falls back to the email, and if there isn't one either, to a
 * middle dot (`·`) — never a question mark, which reads as an error.
 *
 * Uses `codePointAt` and not `[0]`: a name starting with an emoji or a
 * character outside the basic plane would split into two illegible halves
 * with direct indexing, because JavaScript indexes by UTF-16 unit.
 */
export function initialsOf(name?: string | null, email?: string | null): string {
  const source = (name ?? "").trim() || (email ?? "").trim().split("@")[0] || "";
  const parts = source.split(/[\s._-]+/u).filter(Boolean);
  if (parts.length === 0) {
    return "·";
  }
  const first = parts[0] ?? "";
  const last = parts.length > 1 ? (parts.at(-1) ?? "") : "";
  return [first, last]
    .filter(Boolean)
    .slice(0, MAX_INITIALS)
    .map((part) => String.fromCodePoint(part.codePointAt(0) ?? 0).toLocaleUpperCase())
    .join("");
}

/**
 * Stable color index for a person, in `[0, AVATAR_PALETTE_SIZE)`.
 *
 * Derived from the **userId, not the name**: the name changes (a typo gets
 * fixed, a last name is added) and the color must not change with it, or the
 * person "turns into someone else" to whoever recognized them by their color.
 *
 * 32-bit FNV-1a: cheap, dependency-free and with good dispersion for
 * short strings. Not cryptographic and doesn't try to be.
 */
export function avatarPaletteIndex(userId?: string | null): number {
  // WATCH OUT: don't turn this into a default parameter
  // (`userId: string | null = ""`). The pre-commit `oxlint --fix` tries this
  // via `unicorn/prefer-default-parameters`, and it would be a SEMANTIC
  // CHANGE: default values only apply to `undefined`, so a `null`
  // —which is exactly what comes from a row with no user— would pass through
  // and blow up on `.length`.
  const key = typeof userId === "string" ? userId : "";

  let hash = 0x81_1c_9d_c5;
  for (let i = 0; i < key.length; i++) {
    // `charCodeAt` and NOT `codePointAt`: we want to walk UTF-16 units one
    // at a time. `codePointAt` returns the FULL code point on surrogate pairs
    // (and `undefined` past the end), which would change the hash and, with
    // it, the color of people who already had one assigned. The pre-commit
    // `oxlint --fix` also tries this substitution
    // (`unicorn/prefer-code-point`); it is wrong here.
    // oxlint-disable-next-line unicorn/prefer-code-point
    hash ^= key.charCodeAt(i);
    // `Math.imul` keeps the multiplication in 32 bits; with `*` precision is
    // lost past 2^53 and the hash stops being the same across all
    // platforms.
    hash = Math.imul(hash, 0x01_00_01_93);
  }
  return Math.abs(hash) % AVATAR_PALETTE_SIZE;
}

/**
 * Absolute URL of an avatar built from the KEY stored in `user.image`.
 *
 * The row stores the key (`avatars/{userId}/{hash}.webp`) and not an
 * absolute URL, so the row is portable across dev/rc/prod —which have
 * different API origins— and so a domain change doesn't force a
 * rewrite of the database.
 *
 * Tolerates the stored value already being an absolute URL: so a row written
 * by a previous version doesn't look broken.
 */
export function avatarUrl(key?: string | null, apiBaseUrl?: string | null): string | null {
  const value = (key ?? "").trim();
  if (!value) {
    return null;
  }
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }
  const base = (apiBaseUrl ?? "").replace(/\/+$/u, "");
  return `${base}/api/avatar/${value}`;
}
